import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormField } from "@/components/ui/FormField";
import { Button } from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import type { AppRole } from "@/contexts/RoleContext";
import { getV2HomeForRole } from "@/v2/lib/v2Roles";

/**
 * Redirection post-login. La V2 (Skyline OS) est désormais l'app par défaut —
 * on atterrit directement sur la home V2 du rôle (cf. getV2HomeForRole).
 * La V1 reste accessible par URL directe mais n'est plus la cible de connexion.
 */
export function getRedirectForRole(role?: AppRole): string {
  return getV2HomeForRole((role ?? undefined) as AppRole);
}

/** En V2, le prestataire (PEB comme ELS) atterrit sur /v2/missions : la page
 *  missions affiche les missions selon l'assignation, sans router par spécialité. */
export function getRedirectForProfile(profile?: { role?: string; specialites?: string[] | null } | null): string {
  return getRedirectForRole((profile?.role ?? undefined) as AppRole);
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signIn, isAuthenticated, profile } = useAuth();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  // Bouton "Continuer sans connexion" caché par défaut. Apparaît UNIQUEMENT
  // si l URL CONTIENT ?dev a ce render. Pas de persistance sessionStorage
  // (sinon une visite sur /login?dev rendait le bouton visible pour tout
  // l onglet jusqu a la fermeture — confusion potentielle). Thibaud bookmark
  // /login?dev pour son acces V1/V2 ; l externe qui tape /login en dur ne
  // voit jamais le bouton.
  const devVisible = searchParams.has("dev");

  if (isAuthenticated) {
    // ⚠️ Attendre que le profil (donc le rôle) soit chargé avant de rediriger :
    // sinon le rôle est indéfini → getV2HomeForRole tombe sur la home par défaut
    // (/v2/inbox) et un c_level atterrit sur l'inbox au lieu de son dashboard.
    if (!profile) return null;
    return <Navigate to={getRedirectForProfile(profile)} replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorKey(null);
    setLoading(true);
    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        // Les messages Supabase sont en anglais → on les mappe sur des clés i18n.
        const lower = signInError.toLowerCase();
        const key = lower.includes("invalid login credentials")
          ? "auth.invalidCredentials"
          : lower.includes("email not confirmed")
            ? "auth.emailNotConfirmed"
            : "auth.loginError";
        setErrorKey(key);
        setLoading(false);
        return;
      }
      // After successful sign-in, onAuthStateChange in AuthContext fetches the profile.
      // The redirect is handled by the isAuthenticated branch above once profile is loaded.
    } catch {
      setErrorKey("auth.loginError");
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
    >
      <FormField
        label={t("auth.email")}
        value={email}
        onChange={setEmail}
        type="email"
        autoComplete="email"
      />
      <FormField
        label={t("auth.password")}
        value={password}
        onChange={setPassword}
        type="password"
        autoComplete="current-password"
      />

      {errorKey && (
        <p className="text-sm text-red-500">{t(errorKey)}</p>
      )}

      <Button variant="auth" type="submit" disabled={loading}>
        {loading ? t("common.loading") : t("auth.login")}
      </Button>
      <div className="text-center">
        <Link
          to="/forgot-password"
          className="text-xs text-text-muted hover:text-accent"
        >
          {t("auth.forgotPassword")}
        </Link>
      </div>

      {/* Dev login : picker rôle → user, sans password.
          Visible UNIQUEMENT si ?dev dans l URL (ou session déjà flaguée).
          Cf. devVisible plus haut + DEV_LOGIN_FLAG. */}
      {devVisible && (
        <div className="border-t border-border pt-3 mt-1">
          <p className="text-[10px] text-text-muted uppercase tracking-wider text-center mb-2">{t("auth.testPhase")}</p>
          <button
            type="button"
            onClick={() => navigate("/login/select-user?dev")}
            className="w-full px-3 py-2 text-xs font-medium text-accent border border-accent/30 rounded-md hover:bg-accent/5 transition-colors cursor-pointer"
          >
            {t("auth.continueWithout")} →
          </button>
        </div>
      )}
    </form>
  );
}
